import * as React from 'react';
import { Failure as ApiFailure, Request } from '@mam/main/ApiClient';
import { ApiResult } from '@mam/main/ApiHooks/ApiResult';
import { useFetch } from '@mam/main/ApiHooks/useFetch';
import { Initial, Loading } from '@mam/main/Result';

export const useApi = <R extends Request<Response>, Response, Error>(): [
	(request: R) => Promise<ApiResult<Response, Error>>,
	ApiResult<Response, Error>,
	() => void
] => {
	const cancelled = React.useRef(false);
	React.useEffect(
		() => () => {
			cancelled.current = true;
		},
		[]
	);

	const [result, setResult] = React.useState<ApiResult<Response, Error>>(new Initial());
	const updateResult = (result: ApiResult<Response, Error>) => {
		if (!cancelled.current) {
			setResult((prevResult) => (result.isLoading ? result.map((value) => prevResult.value ?? value) : result));
		}
		return result;
	};

	const fetch = useFetch<R, Response, Error>();
	const load = React.useCallback(async (request: R) => {
		setResult((prevResult) => new Loading<Response, ApiFailure<Error>>(prevResult.value));

		const result = await fetch(request);
		if (!cancelled.current) {
			setResult(result);
		}
		return result;
	}, []);

	return [
		load,
		result,
		() => {
			updateResult(new Initial());
		},
	];
};
